'use strict'
import React from 'react'
import {
  makeStyles,
  Box,
  Button,
  Select,
  MenuItem,
  Typography,
  CircularProgress
} from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import SyncIcon from '@material-ui/icons/Sync'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

const LEGACY_FREQUENCY_OPTIONS = [
  { value: '', label: 'Unspecified' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Every Other Day (M-W-F)', label: 'Every Other Day (M-W-F)' },
  { value: 'Twice a Week', label: 'Twice a Week' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Bi-Weekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semi-Annually', label: 'Semi-Annually' },
  { value: 'Annually', label: 'Annually' },
  { value: 'On Demand', label: 'On Demand' }
]

const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const WEEK_TYPE_OPTIONS = [
  { value: '', label: 'Any week' },
  { value: 'A', label: 'Week A' },
  { value: 'B', label: 'Week B' }
]

const sortDays = (days = []) => {
  return [...new Set(days.filter(Boolean))]
    .sort((a, b) => DAY_OPTIONS.indexOf(a) - DAY_OPTIONS.indexOf(b))
}

const useStyles = makeStyles(theme => ({
  detailPanel: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    margin: theme.spacing(0.5, 1)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    minWidth: 90,
    color: theme.palette.text.secondary
  },
  control: {
    minWidth: 160
  },
  controlWide: {
    minWidth: 200
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`
  }
}))

export default function PropertyRowDetail ({
  row,
  crews,
  routes,
  isEditable,
  isLiveMode,
  isDirty,
  isSaving,
  isMoving,
  isSuggesting,
  isResyncing,
  moveTarget,
  onUpdateField,
  onUpdateFrequency,
  onMoveTargetChange,
  onMove,
  onSuggest,
  onSave,
  onResync,
  onCollapse
}) {
  const classes = useStyles()
  const currentAssignment = row.assignedRouteId || row.assignedCrewId
  const canMove = isEditable && !isLiveMode && moveTarget && moveTarget !== currentAssignment && !isMoving
  const moveOptions = (routes && routes.length > 0) ? routes : crews

  return (
    <div className={classes.detailPanel}>
      {/* Frequency row */}
      <div className={classes.row}>
        <Typography className={classes.label}>Frequency:</Typography>
        <Select
          value={row.frequency || ''}
          onChange={(e) => onUpdateFrequency(row.aspirePropertyId, e.target.value)}
          variant="outlined"
          size="small"
          className={classes.controlWide}
          disabled={!isEditable}
        >
          {LEGACY_FREQUENCY_OPTIONS.map(opt => (
            <MenuItem key={opt.value || 'unspecified'} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>

        {(row.frequency === 'Weekly' || row.frequency === 'Bi-Weekly') && (
          <>
            <Typography className={classes.label}>Day:</Typography>
            <Select
              value={row.preferredDay || ''}
              onChange={(e) => onUpdateField(row.aspirePropertyId, 'preferredDay', e.target.value)}
              variant="outlined"
              size="small"
              className={classes.control}
              disabled={!isEditable}
            >
              <MenuItem value="">No day</MenuItem>
              {DAY_OPTIONS.map(day => (
                <MenuItem key={day} value={day}>{day}</MenuItem>
              ))}
            </Select>
          </>
        )}

        {row.frequency === 'Twice a Week' && (
          <>
            <Typography className={classes.label}>Days:</Typography>
            <Select
              multiple
              value={sortDays(row.preferredDays || [])}
              onChange={(e) => {
                const incoming = Array.isArray(e.target.value) ? e.target.value : String(e.target.value || '').split(',')
                const next = sortDays(incoming).slice(0, 2)
                onUpdateField(row.aspirePropertyId, 'preferredDays', next)
                if (!row.preferredDay && next[0]) {
                  onUpdateField(row.aspirePropertyId, 'preferredDay', next[0])
                }
              }}
              variant="outlined"
              size="small"
              className={classes.controlWide}
              disabled={!isEditable}
              renderValue={(selected) => (selected || []).join(', ') || 'No days'}
            >
              {DAY_OPTIONS.map(day => (
                <MenuItem key={day} value={day}>{day}</MenuItem>
              ))}
            </Select>
          </>
        )}

        {row.frequency === 'Bi-Weekly' && (
          <>
            <Typography className={classes.label}>Week Type:</Typography>
            <Select
              value={row.weekType || ''}
              onChange={(e) => onUpdateField(row.aspirePropertyId, 'weekType', e.target.value)}
              variant="outlined"
              size="small"
              className={classes.control}
              disabled={!isEditable}
            >
              {WEEK_TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value || 'none'} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </>
        )}
      </div>

      {/* Lock route row */}
      <div className={classes.row}>
        <Typography className={classes.label}>Lock to route:</Typography>
        <Select
          value={row.lockedRouteId || row.lockedCrewId || ''}
          onChange={(e) => {
            const val = e.target.value
            // If it matches a route, set lockedRouteId; otherwise fall back to lockedCrewId
            const isRoute = (routes || []).some(r => r._id === val)
            if (isRoute) {
              onUpdateField(row.aspirePropertyId, 'lockedRouteId', val)
              // Also set lockedCrewId from the route's crew for backward compat
              const route = (routes || []).find(r => r._id === val)
              if (route?.crewId) onUpdateField(row.aspirePropertyId, 'lockedCrewId', route.crewId)
            } else {
              onUpdateField(row.aspirePropertyId, 'lockedRouteId', '')
              onUpdateField(row.aspirePropertyId, 'lockedCrewId', val)
            }
          }}
          variant="outlined"
          size="small"
          className={classes.controlWide}
          disabled={!isEditable}
        >
          <MenuItem value="">Unlocked</MenuItem>
          {(routes && routes.length > 0 ? routes : crews).map(item => (
            <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
          ))}
        </Select>
      </div>

      {/* Move route row */}
      <div className={classes.row}>
        <Typography className={classes.label}>Move to route:</Typography>
        <Select
          value={moveTarget || ''}
          onChange={(e) => onMoveTargetChange(row.aspirePropertyId, e.target.value)}
          variant="outlined"
          size="small"
          className={classes.controlWide}
          disabled={!isEditable || isLiveMode || isMoving}
        >
          <MenuItem value="" disabled>{isLiveMode ? 'Scenario required' : 'Select route'}</MenuItem>
          {moveOptions.map(item => (
            <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
          ))}
        </Select>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!canMove}
          onClick={() => onMove(row)}
        >
          {isLiveMode ? 'Scenario required' : isMoving ? 'Moving...' : 'Move'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!isEditable || isLiveMode || isSuggesting}
          onClick={() => onSuggest(row)}
        >
          {isSuggesting ? 'Suggesting...' : 'Suggest'}
        </Button>
      </div>

      {/* Last synced + Resync */}
      <div className={classes.row}>
        <Typography className={classes.label}>Last synced:</Typography>
        <Typography variant="body2" style={{ fontSize: '0.82rem', color: '#666' }}>
          {row.lastSyncedAt ? new Date(row.lastSyncedAt).toLocaleString() : 'Never'}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={isResyncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
          disabled={isResyncing}
          onClick={() => onResync(row)}
        >
          {isResyncing ? 'Syncing...' : 'Resync from Aspire'}
        </Button>
      </div>

      {/* Actions */}
      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          disabled={!isEditable || !isDirty || isSaving}
          onClick={() => onSave(row)}
        >
          {isSaving ? 'Saving...' : isLiveMode ? 'Save to Live' : 'Save'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ExpandLessIcon />}
          onClick={onCollapse}
        >
          Collapse
        </Button>
      </Box>
    </div>
  )
}
